import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Building2, Crown } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const money = (v, c = "EUR") => `${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`;

// Admin-only: assign a job to ONE or MORE partner companies with per-firm amounts (€ or % of net).
// The FIRST company is the PRIMARY partner (accepts/refuses, assigns drivers, sees the order);
// the rest are collaborators. The remaining commission (net total − Σ amounts) stays with the platform.
export function MultiPartnerAssign({ order, partners, onSaved }) {
  const cur = order.currency || "EUR";
  const netBase = Number(order.net_base ?? order.pricing?.subtotal ?? order.total ?? 0);
  const [rows, setRows] = useState(() => {
    const existing = (order.partner_assignments || []).map((a) => ({
      partner_id: a.partner_id,
      unit: a.percentage != null ? "pct" : "eur",
      value: a.percentage != null ? String(a.percentage) : (a.amount != null ? String(a.amount) : ""),
    }));
    return existing.length ? existing : [{ partner_id: "", unit: "eur", value: "" }];
  });
  const [saving, setSaving] = useState(false);

  const addRow = () => setRows((r) => [...r, { partner_id: "", unit: "eur", value: "" }]);
  const removeRow = (i) => setRows((r) => (r.length <= 1 ? [{ partner_id: "", unit: "eur", value: "" }] : r.filter((_, idx) => idx !== i)));
  const update = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));

  // Resolve each row to a € amount (percentage rows compute against the net job total).
  const amountOf = (row) => {
    const n = Number(row.value) || 0;
    return row.unit === "pct" ? Math.round(netBase * n) / 100 : Math.round(n * 100) / 100;
  };
  const assignedTotal = rows.reduce((s, r) => s + (r.partner_id ? amountOf(r) : 0), 0);
  const commission = Math.round((netBase - assignedTotal) * 100) / 100;

  // Companies already chosen in OTHER rows are hidden from a row's dropdown (no duplicates).
  const optionsFor = (i) => {
    const taken = new Set(rows.filter((_, idx) => idx !== i).map((r) => r.partner_id).filter(Boolean));
    return partners.filter((p) => !taken.has(p.id));
  };

  const save = async () => {
    const assignments = rows
      .filter((r) => r.partner_id)
      .map((r) => ({
        partner_id: r.partner_id,
        amount: amountOf(r),
        percentage: r.unit === "pct" && r.value !== "" ? Number(r.value) : null,
      }));
    setSaving(true);
    try {
      const { data } = await api.patch(`/orders/${order.id}/partner-assignments`, { assignments });
      toast.success("Assegnazione salvata");
      onSaved?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const filledCount = rows.filter((r) => r.partner_id).length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4 h-fit" data-testid="multi-partner-card">
      <div className="flex items-center gap-2">
        <Building2 size={18} className="text-blue-900" />
        <h3 className="font-display text-lg font-semibold">Aziende partner</h3>
        {filledCount > 0 && (
          <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200" data-testid="mpa-count">
            {filledCount} {filledCount === 1 ? "azienda" : "aziende"}
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500 -mt-2">La prima azienda è la <b>principale</b> (accetta il lavoro, assegna l'autista e vede l'ordine). Le altre collaborano e ricevono solo il loro importo.</p>

      <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm">
        <span className="text-slate-500">Totale job (netto, IVA escl.)</span>
        <span className="font-semibold text-slate-800" data-testid="mpa-net-base">{money(netBase, cur)}</span>
      </div>

      <div className="space-y-3">
        {rows.map((r, i) => {
          const rowAmt = r.partner_id ? amountOf(r) : 0;
          const isPrimary = i === 0;
          return (
            <div key={i} className={`rounded-xl border p-4 space-y-3 ${isPrimary ? "border-blue-300 bg-blue-50/40" : "border-slate-200 bg-slate-50/60"}`} data-testid="mpa-row">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-blue-900">
                  <span className="h-6 w-6 rounded-full bg-blue-900 text-white flex items-center justify-center text-xs">{i + 1}</span>
                  {isPrimary ? (
                    <span className="inline-flex items-center gap-1"><Crown size={14} className="text-amber-500" /> Azienda principale</span>
                  ) : `Azienda ${i + 1}`}
                </span>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600 hover:bg-rose-50" onClick={() => removeRow(i)} data-testid={`mpa-remove-${i}`}><Trash2 size={15} /></Button>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Azienda partner</Label>
                <Select value={r.partner_id} onValueChange={(v) => update(i, "partner_id", v)}>
                  <SelectTrigger data-testid={`mpa-partner-${i}`}><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                  <SelectContent>{optionsFor(i).map((p) => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Quanto paghi a questa azienda</Label>
                <div className="flex items-center gap-2">
                  <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden shrink-0" data-testid={`mpa-unit-${i}`}>
                    <button type="button" onClick={() => update(i, "unit", "eur")}
                      className={`px-3 py-2 text-sm font-medium ${r.unit === "eur" ? "bg-blue-900 text-white" : "bg-white text-slate-600"}`}
                      data-testid={`mpa-unit-eur-${i}`}>{cur}</button>
                    <button type="button" onClick={() => update(i, "unit", "pct")}
                      className={`px-3 py-2 text-sm font-medium ${r.unit === "pct" ? "bg-blue-900 text-white" : "bg-white text-slate-600"}`}
                      data-testid={`mpa-unit-pct-${i}`}>%</button>
                  </div>
                  <Input type="number" min="0" step="0.01" placeholder={r.unit === "pct" ? "es. 30" : "0.00"} value={r.value}
                    onChange={(e) => update(i, "value", e.target.value)} data-testid={`mpa-amount-${i}`} />
                </div>
                {r.unit === "pct" && r.partner_id && (
                  <p className="text-xs text-slate-500" data-testid={`mpa-computed-${i}`}>= {money(rowAmt, cur)}</p>
                )}
              </div>
            </div>
          );
        })}
        <Button size="sm" variant="outline" className="w-full border-dashed" onClick={addRow} data-testid="mpa-add"><Plus size={14} className="mr-1.5" /> Aggiungi un'altra azienda</Button>
      </div>

      <div className="rounded-lg border border-slate-200 p-3 space-y-1.5 text-sm bg-slate-50">
        <div className="flex justify-between"><span className="text-slate-500">Totale alle aziende</span><span className="font-semibold text-blue-800" data-testid="mpa-assigned">{money(assignedTotal, cur)}</span></div>
        <div className="flex justify-between border-t border-slate-200 pt-1.5">
          <span className="text-slate-500">Resta a te (commissione)</span>
          <span className={`font-bold ${commission < 0 ? "text-rose-600" : "text-emerald-700"}`} data-testid="mpa-commission">{money(commission, cur)}</span>
        </div>
        {commission < 0 && (
          <p className="text-xs text-rose-600 pt-1" data-testid="mpa-warning">Attenzione: gli importi superano il totale del job (commissione negativa).</p>
        )}
      </div>

      <Button onClick={save} disabled={saving} className="w-full bg-blue-900 hover:bg-blue-800" data-testid="mpa-save">
        {saving ? "..." : "Salva assegnazione"}
      </Button>
    </div>
  );
}
